import { LitElement, html, css } from 'lit';

export class ConjugationsMode extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .conjugations-container {
      background: white;
      border-radius: 20px;
      padding: 24px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    }

    .section-title {
      font-size: 1.25rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 20px;
      text-align: center;
    }

    .verb-selector {
      margin-bottom: 20px;
    }

    .verb-select {
      width: 100%;
      padding: 14px 18px;
      font-size: 1rem;
      border: 2px solid #e0e0e0;
      border-radius: 12px;
      background: white;
      cursor: pointer;
      transition: all 0.2s ease;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23667eea' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 12px center;
      padding-right: 44px;
    }

    .verb-select:focus {
      outline: none;
      border-color: #667eea;
    }

    .verb-card {
      background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
      border-radius: 16px;
      padding: 20px;
      margin-bottom: 20px;
    }

    .verb-header {
      text-align: center;
      margin-bottom: 16px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(102, 126, 234, 0.2);
    }

    .verb-infinitive {
      font-size: 1.5rem;
      font-weight: 700;
      color: #667eea;
      margin-bottom: 4px;
    }

    .verb-translation {
      font-size: 1rem;
      color: #666;
      font-style: italic;
    }

    .page-info {
      font-size: 0.75rem;
      color: #888;
      margin-top: 8px;
    }

    .tense-tabs {
      display: flex;
      gap: 8px;
      margin-bottom: 16px;
      flex-wrap: wrap;
    }

    .tense-tab {
      flex: 1;
      min-width: 80px;
      padding: 10px 16px;
      border: none;
      border-radius: 10px;
      background: white;
      color: #666;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }

    .tense-tab:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.12);
    }

    .tense-tab.active {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
    }

    .conjugation-table {
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    }

    .conjugation-row {
      display: flex;
      align-items: center;
      padding: 14px 16px;
      border-bottom: 1px solid #f0f0f0;
      transition: background 0.2s ease;
    }

    .conjugation-row:last-child {
      border-bottom: none;
    }

    .conjugation-row:hover {
      background: rgba(102, 126, 234, 0.05);
    }

    .pronoun {
      width: 70px;
      font-weight: 600;
      color: #667eea;
      font-size: 0.9rem;
    }

    .form {
      flex: 1;
      color: #333;
      font-size: 1rem;
      font-weight: 500;
    }

    .loading {
      text-align: center;
      padding: 40px;
      color: #666;
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: #666;
    }

    .empty-state-icon {
      font-size: 3rem;
      margin-bottom: 12px;
    }

    .stats {
      text-align: center;
      color: #888;
      font-size: 0.875rem;
      margin-top: 16px;
    }

    @media (max-width: 480px) {
      .conjugations-container {
        padding: 16px;
      }

      .verb-infinitive {
        font-size: 1.25rem;
      }

      .pronoun {
        width: 60px;
        font-size: 0.8rem;
      }

      .form {
        font-size: 0.9rem;
      }

      .tense-tab {
        padding: 8px 12px;
        font-size: 0.85rem;
      }
    }
  `;

  static properties = {
    allVerbs: { type: Array },
    selectedVerbIndex: { type: Number },
    selectedTense: { type: String },
    isLoading: { type: Boolean },
    refreshKey: { type: Number }
  };

  constructor() {
    super();
    this.allVerbs = [];
    this.selectedVerbIndex = 0;
    this.selectedTense = 'present';
    this.isLoading = true;
    this.refreshKey = 0;
    this._loadConjugations();
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('refreshKey') && this.refreshKey > 0) {
      this.isLoading = true;
      this.allVerbs = [];
      this.selectedVerbIndex = 0;
      this._loadConjugations();
    }
  }

  async _loadConjugations() {
    // Load settings to know which pages to include
    const settings = this._loadSettings();
    const selectedChapters = settings.selectedChapters || [1];
    const selectedPages = settings.selectedPages || {};
    
    this.allVerbs = [];
    
    // Find all conjugation files that match selected pages
    try {
      // Try to load conjugation files for each chapter
      for (const chapter of selectedChapters) {
        let pagesToInclude = selectedPages[chapter];
        
        // If no specific pages selected for this chapter, load all pages from chapter data
        if (!pagesToInclude || pagesToInclude.length === 0) {
          try {
            const chapterResponse = await fetch(`./resources/${chapter}.json`);
            if (chapterResponse.ok) {
              const chapterData = await chapterResponse.json();
              pagesToInclude = chapterData.map(p => p.page);
            } else {
              continue; // Skip if chapter file can't be loaded
            }
          } catch (e) {
            continue; // Skip on error
          }
        }
        
        for (const pageName of pagesToInclude) {
          const filename = `./resources/conj_${pageName}.json`;
          try {
            const response = await fetch(filename);
            if (response.ok) {
              const data = await response.json();
              if (data.verbs && data.verbs.length > 0) {
                // Add page info to each verb
                for (const verb of data.verbs) {
                  this.allVerbs.push({
                    ...verb,
                    page: data.page,
                    chapter: data.chapter
                  });
                }
              }
            }
          } catch (e) {
            // File might not exist, skip
          }
        }
      }
    } catch (error) {
      console.error('Error loading conjugations:', error);
    }
    
    this.isLoading = false;
    
    // If no verbs loaded, try loading all conjugations as fallback
    if (this.allVerbs.length === 0) {
      await this._loadAllConjugations();
    }
  }

  _loadSettings() {
    const saved = localStorage.getItem('vocabSettings');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      selectedChapters: [1],
      selectedPages: {}
    };
  }

  async _loadAllConjugations() {
    // Fallback: load all conjugation files
    const chapters = [1, 2, 3, 4, 5, 6, 7, 8];
    
    for (const chapter of chapters) {
      try {
        // Load chapter file to get page names
        const chapterResponse = await fetch(`./resources/${chapter}.json`);
        if (!chapterResponse.ok) continue;
        
        const chapterData = await chapterResponse.json();
        
        for (const pageData of chapterData) {
          const pageName = pageData.page;
          const filename = `./resources/conj_${pageName}.json`;
          
          try {
            const response = await fetch(filename);
            if (response.ok) {
              const data = await response.json();
              if (data.verbs && data.verbs.length > 0) {
                for (const verb of data.verbs) {
                  this.allVerbs.push({
                    ...verb,
                    page: data.page,
                    chapter: data.chapter
                  });
                }
              }
            }
          } catch (e) {
            // Skip files that don't exist
          }
        }
      } catch (e) {
        console.error(`Error loading chapter ${chapter}:`, e);
      }
    }
    
    this.isLoading = false;
    this.requestUpdate();
  }

  _selectVerb(index) {
    this.selectedVerbIndex = parseInt(index);
  }

  _selectTense(tense) {
    this.selectedTense = tense;
  }

  _getTenseLabel(tense) {
    const labels = {
      'present': 'Presente',
      'passato_prossimo': 'Passato Prossimo',
      'imperfetto': 'Imperfetto'
    };
    return labels[tense] || tense;
  }

  render() {
    if (this.isLoading) {
      return html`
        <div class="conjugations-container">
          <div class="loading">Vervoegingen laden...</div>
        </div>
      `;
    }

    if (!this.allVerbs.length) {
      return html`
        <div class="conjugations-container">
          <div class="empty-state">
            <div class="empty-state-icon">📚</div>
            <p>Geen vervoegingen beschikbaar.<br>Controleer je instellingen om hoofdstukken met werkwoorden te selecteren.</p>
          </div>
        </div>
      `;
    }

    const currentVerb = this.allVerbs[this.selectedVerbIndex];
    const currentConjugations = currentVerb.conjugations[this.selectedTense] || {};

    return html`
      <div class="conjugations-container">
        <div class="section-title">📖 Italiaanse Werkwoordsvervoegingen</div>
        
        <div class="verb-selector">
          <select class="verb-select" @change="${(e) => this._selectVerb(e.target.value)}">
            ${this.allVerbs.map((verb, index) => html`
              <option value="${index}">
                ${verb.infinitive} → ${verb.dutch} (${verb.page})
              </option>
            `)}
          </select>
        </div>

        <div class="verb-card">
          <div class="verb-header">
            <div class="verb-infinitive">${currentVerb.infinitive}</div>
            <div class="verb-translation">${currentVerb.dutch}</div>
            <div class="page-info">Ch. ${currentVerb.chapter} - Page ${currentVerb.page}</div>
          </div>

          <div class="tense-tabs">
            ${Object.keys(currentVerb.conjugations).map(tense => html`
              <button 
                class="tense-tab ${this.selectedTense === tense ? 'active' : ''}"
                @click="${() => this._selectTense(tense)}"
              >
                ${this._getTenseLabel(tense)}
              </button>
            `)}
          </div>

          <div class="conjugation-table">
            ${Object.entries(currentConjugations).map(([pronoun, form]) => html`
              <div class="conjugation-row">
                <div class="pronoun">${pronoun}</div>
                <div class="form">${form}</div>
              </div>
            `)}
          </div>
        </div>

        <div class="stats">
          ${this.allVerbs.length} werkwoorden geladen van geselecteerde hoofdstukken
        </div>
      </div>
    `;
  }
}

customElements.define('conjugations-mode', ConjugationsMode);
